import { useContext } from 'react';
import { isExcursionHidden, orderByExcursion } from '../../shared/excursions';
import { InitialDataContext } from '../initialData';
import type { mediaManifest } from '../../generated/media';
import type { Excursion } from '../../shared/schemas';

export type TourImage = { name: keyof typeof mediaManifest; alt: string };

export type DestinationTour = {
  id: Excursion;
  title: string;
  text: string;
  route: string;
  /** Photo for the catalog card and the excursion page hero; tours without one show a drawn motif. */
  image?: TourImage;
};

export const kttuTours: readonly DestinationTour[] = [
  { id: 'eastern_tram_depot', title: 'Восточное трамвайное депо', text: 'Что происходит с трамваем после рейса? Пройдём между вагонами, познакомимся с историей депо и узнаем, кто готовит транспорт к встрече с пассажирами.', route: '/excursions/vostochnoe-tramvaynoe-depo',
    image: { name: 'vtd-group.jpg', alt: 'Экскурсионная группа в сигнальных жилетах перед жёлтым ретро-трамваем с новогодней ёлкой' } },
  { id: 'city_tram', title: 'Трамвай по городу', text: 'Как выглядит Краснодар из окна трамвая? Посмотрим на знакомые улицы с другого ракурса и поговорим о городе и его транспорте.', route: '/excursions/tramvay-po-gorodu',
    image: { name: 'city-tram-globe-2026.jpg', alt: 'Школьники с экскурсоводом и красными флажками у глобуса «Краснодар 45°»' } },
  { id: 'trolleybus_depot_1', title: 'Первое троллейбусное депо', text: 'Кто готовит троллейбусы к первому рейсу? Познакомимся с работой депо и зададим вопросы о транспорте, который каждый день видим на улицах.', route: '/excursions/pervoe-trolleybusnoe-depo',
    image: { name: 'trolleybus-depot.jpg', alt: 'Экскурсионная группа перед троллейбусами в депо' } },
  { id: 'western_tram_depot', title: 'Западное трамвайное депо', text: 'Как депо готовит трамваи к выходу на линию? Пройдём по территории с экскурсоводом, посмотрим на вагоны и поговорим о людях, без которых трамвай не выйдет в рейс.', route: '/excursions/zapadnoe-tramvaynoe-depo',
    image: { name: 'IMG_0129.JPG', alt: 'Трамвай на веере путей в депо' } },
  { id: 'city_tram_custom', title: 'Трамвай по городу — нестандартный маршрут', text: 'Что хочется рассмотреть за пределами привычного маршрута? Расскажите о своих интересах: программу и маршрут нестандартной поездки обсудим с организатором.', route: '/excursions/tramvay-nestandartnyy-marshrut',
    image: { name: 'city-tram-custom-2026.jpg', alt: 'Дети в сигнальных жилетах машут из салона трамвая' } },
  { id: 'tram_karaoke', title: 'Караоке на колёсах', text: 'Какие песни вы взяли бы в трамвайную поездку? Музыкальный повод собраться и взглянуть на город вместе. Программу и детали участия согласуем заранее.', route: '/excursions/karaoke-na-kolesah' },
];

/** Upcoming KTTU formats: teaser cards only — no page, no booking, not an `Excursion` value. */
export const kttuComingSoon = [{ title: 'Транспортный квест', age: 'от 10 лет' }] as const;

export const botanicalTour: DestinationTour = {
  id: 'botanical_walk',
  title: 'Ботаническая прогулка',
  text: 'Сколько разных листьев можно заметить на знакомой дорожке? Присмотримся к растениям городского парка, научимся различать детали и поговорим о том, как собирать гербарий бережно.',
  route: '/excursions/botanicheskaya-progulka',
};

export const botanicalSteps = [
  { title: 'Прогулка с вопросами', text: 'Чем отличаются листья соседних деревьев? Что подсказывают форма, край и жилки листа? Будем искать ответы по пути.' },
  { title: 'Знакомство с гербарием', text: 'Как сохранить растение и свои наблюдения? Обсудим сбор, сушку и подписи к образцам — чтобы гербарий рассказывал о месте прогулки.' },
  { title: 'Бережное отношение', text: 'Собираем материал только там, где это разрешено правилами парка, и по указаниям экскурсовода. Не повреждаем растения; где сбор запрещён, ограничимся наблюдениями и фотографиями.' },
] as const;

export const botanicalParks = ['Чистяковская роща', 'Солнечный остров', 'Ботанический сад', 'Парк рядом с вами — по согласованию'] as const;

/** Tours the admin has not hidden (seasonal), in the admin's order; reads the server-embedded initial data, so SSR and hydration agree. */
export function useVisibleTours(tours: readonly DestinationTour[]): DestinationTour[] {
  const facts = useContext(InitialDataContext).excursions;
  return orderByExcursion(tours, facts, (tour) => tour.id).filter((tour) => !isExcursionHidden(facts, tour.id));
}

/** True when the admin hid this excursion: its page stays reachable but shows a note instead of booking. */
export function useExcursionHidden(excursion: Excursion): boolean {
  return isExcursionHidden(useContext(InitialDataContext).excursions, excursion);
}
